import { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import { RiMessage2Line } from 'react-icons/ri'
import ImageWithFallback from '@/Component/Common/ImageWithFallback'
import check from '@/assets/images/approved.png';
import DraftModal from './DraftModal';
import { CustomToast } from '@/Utils/CustomToast';
import usePost from '@/Utils/Hooks/usePost';
import { CommonS3BucketUploadAPI, CommonUploadAPI, DeleteDraftAPI } from '@/Utils/APIRequest/Endpoints';
import { ImageLoaderSVG } from '@/Component/Common/SVGs/AuthSVG';
import { useSelector } from 'react-redux';
import { AppStore } from '@/lib/store';
import { DraftCommentSVG, TableDeleteSVG } from '@/Component/Common/SVGs/CommonSVG';
import { useQuery } from '@tanstack/react-query';
import CustomFetch from '@/Utils/APIRequest/AxiosInstance';
import ConfirmationModal from '@/Component/Common/Modal/ConfirmationModal';

const DraftInfo = (props) => {
    const { draftInfoData, JobDetailDataRefetch, draftModalOpen, setDraftModalOpen, uploadDraftModalOpen, setUploadDraftModalOpen, setUploadLoader, JobDetailData, translations, registerUploadTriggerRef } = props;
    const userDetails: any = useSelector((state: AppStore) => state.userDetailSlice?.userDetails);
    const [mediaType, setMediaType] = useState('');
    const [thumbnail, setThumbnail] = useState('');
    const [uploadS3Loader, setUploadS3Loader] = useState(false);
    const [preSignedUrl, setPreSignedUrl] = useState(false);
    const [newFileName, setNewFileName] = useState('');
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [fileParams, setFileParams] = useState({
        root: "files",
        type: "draft",
        fileName: "",
        contentType: "",
    });
    const [draftDeleteConfirmModal, setDraftDeleteConfirmModal] = useState<any>(false);

    // const { mutate: mutateUploadFile, isPending: uploadLoader } = usePost(CommonUploadAPI, null, (response: any) => {
    //     if (response?.status === 200 || response?.status === 201) {
    //         setUploadDraftModalOpen({ responseData: response?.data, campaignInfluencerId: draftInfoData?.id, JobDetailDataRefetch: JobDetailDataRefetch, draftId: draftModalOpen?.id, mediaType: mediaType, thumbnail: thumbnail });
    //     }
    // }, 'POST');

    const { mutate: mutateThumbnailUploadFile, isPending } = usePost(CommonUploadAPI, null, (response: any) => {
        if (response?.status === 200 || response?.status === 201) {
            setThumbnail(response?.data?.fileName);
            setFileParams((prev) => ({
                ...prev,
                fileName: newFileName,
                contentType: selectedFile?.type || "",
            }));
            setPreSignedUrl(true);
        }
    }, 'POST');

    const { data: preSignedUrlData, isLoading: uploadLoader } = useQuery({ queryKey: [CommonS3BucketUploadAPI, fileParams], queryFn: () => CustomFetch({ url: CommonS3BucketUploadAPI, params: { ...fileParams } }, null, false), refetchOnWindowFocus: false, enabled: !!preSignedUrl, select: (data) => data?.data });

    useEffect(() => {
        setUploadLoader(uploadLoader || isPending)
    }, [uploadLoader, isPending])

    const onFileChange = async (event: any) => {
        setNewFileName('');
        if (event?.target?.files && event.target.files.length > 0) {
            const file = event.target.files[0];
            setMediaType(file?.type);
            setSelectedFile(file);

            const renamed = addTimestampToFileName(file.name);
            setNewFileName(renamed);

            const imageMaxSize = 10 * 1024 * 1024; // 10 MB in bytes
            const videoMaxSize = 500 * 1024 * 1024; // 50 MB in bytes

            const allowedImageTypes = ["image/jpeg", "image/png", "image/webp", "image/svg+xml", "image/heic", "image/heif"];
            const allowedVideoTypes = ["video/mp4", "video/mov", "video/mkv", "video/quicktime", "video/x-msvideo", "video/x-matroska", "video/webm"];

            // Check file type
            if (![...allowedImageTypes, ...allowedVideoTypes].includes(file.type)) {
                CustomToast("error", translations?.draftMediaValidation);
                return;
            }

            // Check file size
            if (allowedImageTypes.includes(file.type) && file.size > imageMaxSize) {
                CustomToast("error", translations?.draftImageValidation.replace(":imageSize", (imageMaxSize / 1024 / 1024)));
                return;
            }

            if (allowedVideoTypes.includes(file.type) && file.size > videoMaxSize) {
                CustomToast("error", translations?.draftVideoValidation.replace(":videoSize", (videoMaxSize / 1024 / 1024)));
                return;
            }

            // let formData: any = new FormData()
            // formData.append('file', file);
            // formData.append('type', 'draft');
            // formData.append('root', "files");
            let body: any = { root: "files", type: "draft", fileName: renamed, contentType: file?.type };

            // setFileParams(body);
            // setPreSignedUrl(true);
            // mutateUploadFile();

            if (file?.type?.includes('video')) {
                extractVideoThumbnail(file);
            } else {
                setFileParams(body);
                setPreSignedUrl(true);
            }
        }
    };

    const addTimestampToFileName = (fileName: string) => {
        const safeName = fileName.replace(/\s+/g, "_");
        const timestamp = new Date().toISOString();
        const dotIndex = safeName.lastIndexOf(".");
        if (dotIndex === -1) {
            return `${fileName}_${timestamp}`;
        }
        const name = fileName.substring(0, dotIndex);
        const ext = fileName.substring(dotIndex);
        return `${name}_${timestamp}${ext}`;
    }

    useEffect(() => {
        const uploadToS3 = async () => {
            if (!selectedFile || !preSignedUrlData || !newFileName) return;

            if (fileParams.fileName !== newFileName) return;

            setUploadS3Loader(true);

            try {
                const upload = await fetch(preSignedUrlData?.url, {
                    method: "PUT",
                    headers: {
                        "Content-Type": selectedFile.type
                    },
                    body: selectedFile,
                });

                if (upload.ok) {
                    console.log("path", userDetails?.draft_path + newFileName, thumbnail);

                    setUploadDraftModalOpen({
                        responseData: { filepath: (userDetails?.draft_path + newFileName), fileName: newFileName },
                        campaignInfluencerId: draftInfoData?.id,
                        JobDetailDataRefetch,
                        draftId: draftModalOpen?.id,
                        mediaType,
                        thumbnail,
                    });
                } else {
                    CustomToast("error", "❌ Upload failed");
                }
            } catch (err) {
                console.error(err);
                CustomToast("error", "Upload error occurred");
            } finally {
                setUploadS3Loader(false);
            }
        };
        uploadToS3();
    }, [newFileName, preSignedUrlData, thumbnail]);

    const extractVideoThumbnail = (videoFile) => {
        const video = document.createElement("video");
        const canvas = document.createElement("canvas");
        const ctx: any = canvas.getContext("2d");
        const objectUrl = URL.createObjectURL(videoFile);

        video.src = objectUrl;
        video.crossOrigin = "anonymous";
        video.muted = true;
        video.playsInline = true;
        video.preload = "metadata";
        video.setAttribute("playsinline", "true");
        video.setAttribute("webkit-playsinline", "true");

        let thumbnailExtracted = false;
        let timeoutId: any = null;

        const cleanup = () => {
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };

        const proceedWithoutThumbnail = () => {
            if (thumbnailExtracted) return;
            cleanup();
            setFileParams((prev) => ({
                ...prev,
                fileName: newFileName,
                contentType: selectedFile?.type || "",
            }));
            setPreSignedUrl(true);
        };

        const extractFrame = () => {
            if (thumbnailExtracted) return;
            thumbnailExtracted = true;

            try {
                // Set canvas size to video size
                if (video.videoWidth > 0 && video.videoHeight > 0) {
                    canvas.width = video.videoWidth;
                    canvas.height = video.videoHeight;

                    // Draw the video frame onto the canvas
                    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

                    // Convert canvas to an image URL (base64)
                    const thumbnailBase64 = canvas.toDataURL("image/jpeg");

                    console.log("Thumbnail extracted:", thumbnailBase64);
                    base64ToBlob(thumbnailBase64);
                    cleanup();
                } else {
                    // Fallback: try to extract at current time
                    if (video.readyState >= 2) {
                        canvas.width = 640;
                        canvas.height = 360;
                        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                        const thumbnailBase64 = canvas.toDataURL("image/jpeg");
                        console.log("Thumbnail extracted (fallback):", thumbnailBase64);
                        base64ToBlob(thumbnailBase64);
                        cleanup();
                    } else {
                        proceedWithoutThumbnail();
                    }
                }
            } catch (error) {
                console.error("Error extracting thumbnail:", error);
                proceedWithoutThumbnail();
            }
        };

        // Try to load metadata first
        video.addEventListener("loadedmetadata", () => {
            try {
                // Seek to a small time (0.1s) or use first frame if video is very short
                if (video.duration && video.duration > 0) {
                    const seekTime = Math.min(0.1, video.duration / 2 || 0.1);
                    video.currentTime = seekTime;
                } else {
                    // If duration is not available, try to extract at current position
                    extractFrame();
                }
            } catch (error) {
                console.error("Error seeking video:", error);
                extractFrame();
            }
        }, { once: true });

        video.addEventListener("loadeddata", () => {
            // If metadata wasn't loaded, try to extract frame at current position
            if (video.readyState >= 2 && !thumbnailExtracted) {
                // Small delay to ensure frame is ready
                setTimeout(() => {
                    if (!thumbnailExtracted) {
                        extractFrame();
                    }
                }, 100);
            }
        }, { once: true });

        video.addEventListener("seeked", () => {
            extractFrame();
        }, { once: true });

        // Handle video errors - fallback to proceed without thumbnail
        video.addEventListener("error", (error) => {
            console.error("Error loading video: ", error);
            proceedWithoutThumbnail();
        }, { once: true });

        // Fallback timeout - if video doesn't load in 5 seconds, proceed without thumbnail
        timeoutId = setTimeout(() => {
            if (!thumbnailExtracted) {
                if (video.readyState >= 2) {
                    extractFrame();
                } else {
                    console.warn("Video thumbnail extraction timeout, proceeding without thumbnail");
                    proceedWithoutThumbnail();
                }
            }
        }, 5000);

        // Load the video
        video.load();
    };

    // Convert Base64 to Blob
    const base64ToBlob = (base64Data) => {
        console.log("base64Data", base64Data);

        const byteString = atob(base64Data.split(",")[1]);
        const mimeType = base64Data.split(",")[0].split(":")[1].split(";")[0]; // Get MIME type
        const arrayBuffer = new ArrayBuffer(byteString.length);
        const uintArray = new Uint8Array(arrayBuffer);

        for (let i = 0; i < byteString.length; i++) {
            uintArray[i] = byteString.charCodeAt(i);
        }

        const blob = new Blob([uintArray], { type: mimeType });

        // Convert Blob to File
        const file = new File([blob], `thumbnail_${Date.now()}.jpg`, { type: mimeType });

        // Upload file
        let formData: any = new FormData();
        formData.append('file', file);
        formData.append('type', 'draft');
        formData.append('root', "files");

        mutateThumbnailUploadFile(formData);
    };

    const fileInputRef = useRef<any>(null);

    useEffect(() => {
        if (registerUploadTriggerRef) {
            const triggerFn = () => {
                fileInputRef.current?.click();
            };

            registerUploadTriggerRef(triggerFn);
        }
    }, [registerUploadTriggerRef]);

    const { mutate: mutateDeleteDraft, isPending: deleteDraftLoader } = usePost(`${DeleteDraftAPI}/${draftDeleteConfirmModal?.id}`, null, (response) => {
        if (response?.status == 200 || response?.status == 201) {
            CustomToast("success", "Draft deleted successfully");
            JobDetailDataRefetch();
            setDraftDeleteConfirmModal(false);
        }
    }, 'delete');

    const handleDeleteDraft = (e: any, draftId: any) => {
        e.stopPropagation();
        setDraftDeleteConfirmModal({ id: draftId });
    }

    const handleConfirmDeleteDraft = () => {
        mutateDeleteDraft();
    }

    return (
        <>
            <div className='grid md:grid-cols-4 grid-cols-3 gap-4 mt-2.5 items-start'>
                {(userDetails?.role == "influencer" && JobDetailData?.status == "running" && JobDetailData?.campaignInfluencers?.[0]?.status !== "closed") &&
                    <div className='w-full aspect-square flex items-center justify-center cursor-pointer'>
                        {(uploadLoader || uploadS3Loader) ? <ImageLoaderSVG /> :
                            <>
                                <input type='file' ref={fileInputRef} hidden id='image' accept="image/jpeg,image/png,image/webp,image/svg+xml,image/heic,image/heif,video/mp4,video/quicktime,video/mov,video/mkv,video/x-matroska,video/webm,.jpg,.jpeg,.png,.webp,.svg,.mp4,.mov,.mkv" onChange={onFileChange} />
                                <label className='flex items-center justify-center w-full h-full cursor-pointer bg-lightGray/50 border border-dashed border-gray' htmlFor='image'>
                                    <Image src={'/assets/images/plus-black.svg'} alt="Profile 1" className='w-7 h-7' width={28} height={28} />
                                </label>
                            </>
                        }
                    </div>
                }
                {draftInfoData?.drafts?.length > 0 ? draftInfoData?.drafts?.map((item, index) => (
                    <div key={index} onClick={() => setDraftModalOpen({ item, draftInfoData })} className='flex flex-col gap-1.5 cursor-pointer relative'>
                        {item?.status === 'pending' && userDetails?.role == "influencer" && (
                            <div className='flex items-center gap-1.5 absolute right-1 top-1 p-1 bg-white rounded-full cursor-pointer' onClick={(e) => handleDeleteDraft(e, item?.id)}>
                                <TableDeleteSVG customClass='w-4 h-4' />
                            </div>
                        )}
                        <div className='w-full h-max aspect-square bg-lightGray/50 mb-1'>
                            <ImageWithFallback src={`${userDetails?.draft_path}${item?.draftVersions?.[0].type == 'video' ? item?.draftVersions?.[0]?.thumbnail : item?.draftVersions?.[0]?.draft_name}`} alt="Profile 1" className='w-full h-full object-cover' width={125} height={125} />
                        </div>
                        <div className='flex items-center gap-1.5'>
                            <DraftCommentSVG />
                            <span className='text-darkGray text-xs'>{item?.comment_count} {translations?.comments}</span>
                        </div>
                        {item?.status === 'pending' && (
                            <div className='flex items-center gap-1.5'>
                                <span className='text-waitingQuote text-xs'>{translations?.pending}</span>
                            </div>
                        )}
                        {item?.status === 'approved' && (
                            <div className='flex items-center gap-1.5'>
                                <Image src={check} alt="Profile 1" className='w-4 h-4' width={16} height={16} />
                                <span className='text-darkGray text-xs'>{translations?.approved}</span>
                            </div>
                        )}
                    </div>
                )) : ''}
            </div>
            {draftDeleteConfirmModal && <ConfirmationModal modal={draftDeleteConfirmModal} setModal={setDraftDeleteConfirmModal} loader={deleteDraftLoader} args={{ message: translations?.deleteDraftMessage }} callbackFunction={handleConfirmDeleteDraft} />}
        </>
    )
}

export default DraftInfo
